import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Headphones, PhoneCall, X, Loader2, MessageSquare, ShieldCheck, Sparkles } from 'lucide-react';
import { createSupportRequest } from '../api';

function normalizePhone(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  return digits;
}

const SupportWidget = ({ showNotification, open: controlledOpen, onOpenChange, showFloatingButton = true }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const modalRef = useRef(null);

  const isLoggedIn = useMemo(() => Boolean(localStorage.getItem('token')), []);
  const role = useMemo(() => localStorage.getItem('role')?.toLowerCase() || '', []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const canSubmit = useMemo(() => {
    const digits = normalizePhone(phone);
    // Logged-in users can submit without typing phone; backend will use profile phone if available.
    if (isLoggedIn) return true;
    return digits.length >= 8;
  }, [phone, isLoggedIn]);

  const submit = useCallback(async () => {
    if (!canSubmit || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        phone: normalizePhone(phone || ''),
        requestCallback: true,
        message: String(message || '').trim(),
        source: window.location?.hash || window.location?.pathname || '',
        metadata: {
          href: window.location?.href || '',
          userAgent: navigator.userAgent,
          role: role || undefined,
        },
      };
      await createSupportRequest(payload, token);
      showNotification?.('Support request submitted. We will reach out shortly.', 'success');
      setOpen(false);
      setPhone('');
      setMessage('');
    } catch (err) {
      showNotification?.(err?.response?.data?.message || err?.message || 'Failed to submit support request', 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmit, isSubmitting, message, phone, role, showNotification]);

  return (
    <>
      {showFloatingButton && (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-[120] group"
        aria-label="Need support"
      >
        <div className="relative">
          <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 blur opacity-50 group-hover:opacity-80 transition" />

          {/* Mobile: icon-only */}
          <div className="relative sm:hidden w-12 h-12 rounded-2xl bg-white shadow-xl border border-purple-100 flex items-center justify-center">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 text-white flex items-center justify-center shadow">
              <Headphones size={18} />
            </div>
          </div>

          {/* Desktop+: compact pill */}
          <div className="relative hidden sm:flex px-4 py-3 rounded-2xl bg-white shadow-xl border border-purple-100 items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 text-white flex items-center justify-center shadow">
              <Headphones size={18} />
            </div>
            <div className="text-left leading-tight">
              <div className="text-sm font-extrabold text-gray-900 flex items-center gap-1">
                Need support
                <Sparkles size={14} className="text-purple-600" />
              </div>
              <div className="text-xs text-gray-500 font-semibold">Request a callback</div>
            </div>
          </div>
        </div>
      </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div
            ref={modalRef}
            className="w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-purple-100 overflow-hidden"
            role="dialog"
            aria-modal="true"
          >
            <div className="p-5 sm:p-6 bg-gradient-to-br from-purple-600 to-pink-600 text-white">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center">
                      <PhoneCall size={18} />
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-extrabold">Request support</h3>
                      <p className="text-xs sm:text-sm text-white/85 mt-0.5">
                        Tell us what you need — we’ll get back quickly.
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="p-2 rounded-xl hover:bg-white/10 transition"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="p-5 sm:p-6 space-y-4">
              <div className="flex items-center gap-2 p-3 rounded-2xl bg-purple-50 border border-purple-100 text-sm font-extrabold text-purple-900">
                <PhoneCall size={16} />
                Request a callback
              </div>

              <div className="space-y-2">
                <label className="text-xs font-extrabold text-gray-700">
                  Mobile number {isLoggedIn ? '(optional)' : '(required)'}
                </label>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  inputMode="tel"
                  placeholder={isLoggedIn ? 'Optional — use a different number' : 'Enter mobile number'}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400 text-sm"
                />
                {!isLoggedIn ? (
                  <div className="text-[11px] text-gray-500 font-semibold">
                    Not logged in — we’ll use this number to reach you.
                  </div>
                ) : (
                  <div className="text-[11px] text-gray-500 font-semibold">
                    Logged in — we’ll use your profile number if you leave this blank.
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-xs font-extrabold text-gray-700">What do you need?</label>
                <div className="relative">
                  <MessageSquare size={16} className="absolute left-4 top-4 text-gray-400" />
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Example: I’m unable to generate kits / payment failed / need help uploading fabrics…"
                    rows={4}
                    className="w-full pl-11 pr-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400 text-sm resize-none"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 pt-2">
                <div className="text-[11px] text-gray-500 font-semibold flex items-center gap-1">
                  <ShieldCheck size={14} className="text-gray-400" />
                  Saved for admins to review
                </div>
                <button
                  type="button"
                  onClick={submit}
                  disabled={!canSubmit || isSubmitting}
                  className={`px-5 py-3 rounded-2xl text-sm font-extrabold transition-all ${
                    !canSubmit || isSubmitting
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow hover:shadow-lg'
                  }`}
                >
                  {isSubmitting ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin" />
                      Submitting…
                    </span>
                  ) : (
                    'Submit'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SupportWidget;

